import { ReactNode, useCallback, useContext, useEffect, useMemo } from "react";
import {
  EntityConstants,
  GenericEntity,
  ISessionedEntity,
  ISessionedEntityStartSessionParameters,
  ISessionedEntityStopSessionParameters,
  IPermissionedEntity,
} from "../../../api/GenericTypes";
import { SessionContext } from "../../contexts/SessionContext";
import { FetchStatus, QueryStatus, useQueryClient } from "@tanstack/react-query";
import { useEntityDetail } from "../../../api/BaseEntityApi";
import { Alert } from "../../overlays/Alert/Alert";
import { LoadingWrapper } from "../../LoadingWrapper";
import { AlertModal } from "../../modals/AlertModal/AlertModal";
import { GetPersons } from "../../misc/EntityRenders/EntityRenderer";
import { DateTimeRenderer } from "../../datetime/DateTimeFormatter";
import { useHistory } from "react-router-dom";
import { LiveConnectionCallbacks, useListen } from "../../signalr/SignalRContext";
import { HubConnection } from "@microsoft/signalr";

interface SessionedEntityReturnValues extends Partial<HubConnection> {
  startSession: (forceTakeOver?: boolean) => Promise<void>;
  stopSession: () => Promise<void>;
  isCurrentlyEdited: boolean;
  entity?: GenericEntity & ISessionedEntity & IPermissionedEntity;
  status: QueryStatus;
  fetchStatus: FetchStatus;
}
export const useStartEntityEditSession: ({
  entityId,
  entityConstants,
}: {
  entityId: number | string;
  entityConstants: EntityConstants;
}) => SessionedEntityReturnValues = ({ entityId, entityConstants }) => {
  const { session, api } = useContext(SessionContext);

  // Subscribe to live updates
  const queryClient = useQueryClient();

  const callback: LiveConnectionCallbacks["EntityEditSessionStateChanged"] = useCallback(
    (entryId, publicSessionId, isCurrentlyEdited) => {
      if (entryId === entityId) {
        queryClient.invalidateQueries({ queryKey: [entityConstants.resource, "detail", entityId] });
      }
    },
    [entityId, entityConstants.resource, queryClient]
  );
  const hub = useListen("EntityEditSessionStateChanged", callback);

  // Fetch entity to get session status
  const {
    data: entity,
    status,
    fetchStatus,
    isFetching,
  } = useEntityDetail<GenericEntity & ISessionedEntity & IPermissionedEntity>(
    entityConstants.resource,
    entityId,
    undefined,
    { enabled: true, cacheTime: 0, staleTime: 0 }
  );

  const isCurrentlyEdited = useMemo(
    () => !entity || (!!entity?.isCurrentlyEdited && entity?.publicSessionId !== session?.publicSessionId),
    [entity, session?.publicSessionId]
  );

  // Helper functions
  const startSession = useCallback(
    async (forceTakeOver: boolean = false) => {
      if (!session?.publicSessionId) return;
      if (!hub?.connectionId) return;
      var parameters: ISessionedEntityStartSessionParameters = {
        publicSessionId: session.publicSessionId,
        forceTakeOver,
        connectionId: hub.connectionId,
      };
      await api
        .post(`${entityConstants.resource}/${entityId}/session/start`, parameters)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: [entityConstants.resource, "detail", entityId] });
        })
        .catch((e) => console.error(e));
    },
    [api, entityConstants.resource, entityId, hub?.connectionId, queryClient, session?.publicSessionId]
  );

  const stopSession = useCallback(async () => {
    if (!session?.publicSessionId) return;
    if (!hub?.connectionId) return;
    var parameters: ISessionedEntityStopSessionParameters = { publicSessionId: session.publicSessionId };
    await api
      .post(`${entityConstants.resource}/${entityId}/session/end`, parameters)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: [entityConstants.resource, "detail", entityId] });
      })
      .catch((e) => console.error(e));
  }, [api, entityConstants.resource, entityId, hub?.connectionId, queryClient, session?.publicSessionId]);

  // Check entity session status on page load
  useEffect(() => {
    if (!!entity) {
      if (!entity.publicSessionId) {
        startSession();
      }
    }
  }, [entity, startSession, session?.publicSessionId, stopSession]);

  // Stop entity session on page unload/refresh or going to another page
  const stopSessionOnUnload = useCallback(
    (ev) => {
      stopSession();
    },
    [stopSession]
  );
  useEffect(() => {
    window.addEventListener("beforeunload", stopSessionOnUnload);
    return () => {
      window.removeEventListener("beforeunload", stopSessionOnUnload);
      stopSession();
    };
  }, [stopSession, stopSessionOnUnload]);

  return {
    startSession: startSession,
    stopSession: stopSession,
    isCurrentlyEdited,
    status,
    fetchStatus: isFetching ? "fetching" : fetchStatus,
    entity,
    ...hub,
  };
};

export const EntityEditSessionWrapper = ({
  entityId,
  entityConstants,
  children,
}: {
  entityId: number | string;
  entityConstants: EntityConstants;
  children: ReactNode;
}) => {
  const { session } = useContext(SessionContext);
  const history = useHistory();
  const { startSession, isCurrentlyEdited, status, fetchStatus, entity } = useStartEntityEditSession({
    entityConstants,
    entityId,
  });

  const proceedCallback = useCallback(() => {
    startSession(true);
  }, [startSession]);

  if (!session?.publicSessionId) return <Alert fit centered message="Public session id not found" type="danger" />;
  return (
    <LoadingWrapper status={status} fetchStatus={fetchStatus}>
      {isCurrentlyEdited ? (
        <>
          {!!entity?.isCurrentlyEdited ? (
            <>
              <Alert
                fit
                centered
                message={`This ${entityConstants.entitySingular} is currently being edited by another user`}
                type="warning"
              />
              {!!entity?.permissions?.canEdit && (
                <AlertModal
                  title={`This ${entityConstants.entitySingular} is currently being edited`}
                  description={
                    <div>
                      <div
                        style={{
                          paddingBottom: entity?.currentlyEditedBy || entity?.currentlyEditedSince ? "15px" : "0",
                        }}
                      >
                        Proceeding will end the other user's session and allow you to edit this item. Are you sure you
                        want to proceed?
                      </div>
                      {entity?.currentlyEditedBy && (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "2fr 3fr",
                            gap: "5px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            paddingRight: "10px",
                            paddingBottom: entity?.currentlyEditedSince ? "5px" : undefined,
                          }}
                        >
                          <h5 style={{ whiteSpace: "nowrap", padding: 0, margin: 0 }}>Currently edited by:</h5>
                          <div>
                            <GetPersons persons={[entity?.currentlyEditedBy]} />
                          </div>
                        </div>
                      )}
                      {entity?.currentlyEditedSince && (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "2fr 3fr",
                            gap: "5px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            paddingRight: "10px",
                            paddingBottom: entity?.modifiedOn ? "5px" : undefined,
                          }}
                        >
                          <h5 style={{ whiteSpace: "nowrap", padding: 0, margin: 0 }}>Session started on:</h5>
                          <div>
                            <DateTimeRenderer date={entity?.currentlyEditedSince} includeDate includeTime />
                          </div>
                        </div>
                      )}
                      {entity?.modifiedOn && (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "2fr 3fr",
                            gap: "5px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            paddingRight: "10px",
                          }}
                        >
                          <h5 style={{ whiteSpace: "nowrap", padding: 0, margin: 0 }}>Last modified on:</h5>
                          <div>
                            <DateTimeRenderer date={entity?.modifiedOn} includeDate includeTime />
                          </div>
                        </div>
                      )}
                    </div>
                  }
                  proceedLabel="Confirm"
                  showModal
                  setShowModal={() => history.goBack()}
                  onProceed={proceedCallback}
                  type="warning"
                  forceCountdown
                />
              )}
            </>
          ) : null}
        </>
      ) : (
        children
      )}
    </LoadingWrapper>
  );
};
